import React, { useState, useEffect, useRef } from 'react';
import { api, API, formatCurrency, formatDate } from '../lib/utils';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useIsMobile } from '../lib/responsive';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Autocomplete } from '../components/ui/autocomplete';
import { ChefHat, Plus, IndianRupee, TrendingUp, TrendingDown, AlertTriangle, Search, Pencil, Trash2, Upload, FileText, Loader2, Monitor, FolderOpen, Eye, Download, Calendar, Building2, MapPin, X } from 'lucide-react';

export default function Ingredients() {
  const { user } = useAuth();
  const canCreate = user?.permissions?.can_create !== false;
  const canEdit = user?.permissions?.can_edit !== false;
  const canDelete = user?.permissions?.can_delete !== false;
  const [purchases, setPurchases] = useState([]);
  const [marginAlerts, setMarginAlerts] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [billUploadOpen, setBillUploadOpen] = useState(false);
  const [vendorLibrary, setVendorLibrary] = useState([]);
  const [ingredientLibrary, setIngredientLibrary] = useState([]);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('purchases');
  const isAdmin = user?.role === 'owner';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, alertsRes, venturesRes, locationsRes, vendorsRes, ingredientsRes] = await Promise.all([
        api.get('/ingredients'), api.get('/ingredients/margin-radar').catch(() => ({ data: [] })),
        api.get('/ventures'), api.get('/locations'),
        api.get('/library/vendors').catch(() => ({ data: [] })),
        api.get('/library/ingredients').catch(() => ({ data: [] }))
      ]);
      setPurchases(purchasesRes.data);
      setMarginAlerts(alertsRes.data || []);
      setVentures(venturesRes.data);
      setLocations(locationsRes.data);
      setVendorLibrary(vendorsRes.data || []);
      setIngredientLibrary(ingredientsRes.data || []);
    } catch { toast.error('Failed to load ingredients'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete purchase of ${p.ingredient_name}?`)) return;
    try { await api.delete(`/ingredients/${p.purchase_id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || 'Unknown';
  const totalSpent = purchases.reduce((sum, p) => sum + p.price, 0);
  const ingredientSummary = purchases.reduce((acc, p) => {
    const name = p.ingredient_name.toLowerCase();
    if (!acc[name]) acc[name] = { name: p.ingredient_name, totalSpent: 0, purchases: [] };
    acc[name].totalSpent += p.price;
    acc[name].purchases.push(p);
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="ingredients-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Ingredients</h1>
          <p className="text-zinc-500 mt-1">Track ingredient purchases and price changes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBillUploadOpen(true)} className="rounded-xl" data-testid="upload-bill-btn">
            <Upload className="w-4 h-4 mr-2" />Upload Bill
          </Button>
          {canCreate && <Button onClick={() => { setEditItem(null); setAddDialogOpen(true); }} className="rounded-xl" data-testid="add-ingredient-btn">
            <Plus className="w-4 h-4 mr-2" />Add Purchase
          </Button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        <button
          onClick={() => setActiveTab('purchases')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'purchases' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}
          data-testid="tab-purchases"
        >
          <ChefHat className="w-4 h-4 inline mr-1.5 -mt-0.5" />Purchases
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'library' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}
            data-testid="tab-bill-library"
          >
            <FolderOpen className="w-4 h-4 inline mr-1.5 -mt-0.5" />Bill Library
          </button>
        )}
      </div>

      {activeTab === 'purchases' && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="roch-card metric-card"><div className="metric-label">Total Ingredient Spend</div><div className="metric-value currency">{formatCurrency(totalSpent)}</div><div className="text-xs text-zinc-500 mt-2">{purchases.length} purchases recorded</div></Card>
        <Card className="roch-card metric-card"><div className="metric-label">Unique Ingredients</div><div className="metric-value">{Object.keys(ingredientSummary).length}</div></Card>
        <Card className="roch-card metric-card"><div className="metric-label">Active Vendors</div><div className="metric-value">{new Set(purchases.map(p => p.vendor)).size}</div></Card>
      </div>

      {marginAlerts.length > 0 && (
        <Card className="roch-card mb-8">
          <CardHeader className="pb-2"><CardTitle className="text-base font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Margin Radar Alerts</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {marginAlerts.map((alert, index) => (
                <div key={index} className={`alert-card ${alert.direction === 'up' || alert.type === 'cost_ratio' ? 'warning' : 'info'}`}>
                  {alert.direction === 'up' ? <TrendingUp className="w-4 h-4 text-amber-500 flex-shrink-0" /> : alert.direction === 'down' ? <TrendingDown className="w-4 h-4 text-green-500 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                  <div className="text-sm">
                    {alert.type === 'price_change' ? (<><span className="font-medium">{alert.ingredient}</span> price {alert.direction === 'up' ? ' increased ' : ' decreased '}<span className={alert.direction === 'up' ? 'text-amber-600 font-medium' : 'text-green-600 font-medium'}>{Math.abs(alert.change_percent)}%</span><div className="text-xs text-zinc-500 mt-1">{formatCurrency(alert.first_price)}/unit to {formatCurrency(alert.last_price)}/unit</div></>) : (<span className="font-medium">{alert.message}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="roch-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">Recent Purchases</CardTitle>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input placeholder="Search ingredient, vendor..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="ingredients-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {purchases.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon"><ChefHat className="w-6 h-6" /></div><h3 className="font-medium text-zinc-900 mb-1">No purchases recorded</h3><p className="text-sm text-zinc-500">Start tracking your ingredient purchases</p></div>
          ) : (
            <div className="roch-table-wrap">
            <table className="roch-table">
              <thead><tr><th>Date</th><th>Ingredient</th><th>Vendor</th><th>Qty</th><th>Venture</th><th className="text-right">Price</th><th className="text-right">Per Unit</th><th className="w-20"></th></tr></thead>
              <tbody>
                {purchases.filter(p => {
                  if (!searchQuery) return true;
                  const q = searchQuery.toLowerCase();
                  return p.ingredient_name?.toLowerCase().includes(q) || p.vendor?.toLowerCase().includes(q);
                }).slice(0, 50).map((p) => (
                  <tr key={p.purchase_id}>
                    <td className="text-zinc-500">{formatDate(p.date)}</td>
                    <td className="font-medium">{p.ingredient_name}</td>
                    <td className="text-zinc-600">{p.vendor}</td>
                    <td className="text-zinc-600">{p.quantity} {p.unit}</td>
                    <td className="text-zinc-600">{getVentureName(p.venture_id)}</td>
                    <td className="text-right font-medium">{formatCurrency(p.price)}</td>
                    <td className="text-right text-zinc-500">{formatCurrency(p.price_per_unit)}/{p.unit}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditItem(p); setAddDialogOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>}
                        {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(p)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddPurchaseDialog open={addDialogOpen} onOpenChange={(v) => { setAddDialogOpen(v); if (!v) setEditItem(null); }} ventures={ventures} locations={locations} onSuccess={fetchData} editItem={editItem} vendorLibrary={vendorLibrary} ingredientLibrary={ingredientLibrary} />
      <BillUploadDialog open={billUploadOpen} onOpenChange={setBillUploadOpen} ventures={ventures} locations={locations} onSuccess={fetchData} vendorLibrary={vendorLibrary} />
      </>)}

      {activeTab === 'library' && isAdmin && (
        <BillLibrary ventures={ventures} locations={locations} />
      )}
    </div>
  );
}

function AddPurchaseDialog({ open, onOpenChange, ventures, locations, onSuccess, editItem, vendorLibrary = [], ingredientLibrary = [] }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ingredient_name: '', vendor: '', quantity: '', unit: 'kg', price: '', venture_id: '', location_id: '', date: new Date().toISOString().split('T')[0] });
  const units = ['kg', 'g', 'L', 'ml', 'pcs', 'dozen', 'box', 'packet'];

  // Create suggestion lists from library data
  const vendorSuggestions = vendorLibrary.map(v => v.name);
  const ingredientSuggestions = ingredientLibrary.map(i => i.name);

  useEffect(() => {
    if (editItem) {
      setForm({ ingredient_name: editItem.ingredient_name || '', vendor: editItem.vendor || '', quantity: String(editItem.quantity || ''), unit: editItem.unit || 'kg', price: String(editItem.price || ''), venture_id: editItem.venture_id || '', location_id: editItem.location_id || '', date: editItem.date || '' });
    } else {
      setForm({ ingredient_name: '', vendor: '', quantity: '', unit: 'kg', price: '', venture_id: '', location_id: '', date: new Date().toISOString().split('T')[0] });
    }
  }, [editItem, open]);

  const filteredLocations = locations.filter(l => l.venture_id === form.venture_id);

  // Auto-fill unit and price when ingredient is selected from library
  const handleIngredientChange = (value) => {
    setForm(prev => ({ ...prev, ingredient_name: value }));
    // Find ingredient in library to auto-fill unit
    const libItem = ingredientLibrary.find(i => i.name.toLowerCase() === value.toLowerCase());
    if (libItem && libItem.unit) {
      setForm(prev => ({ ...prev, unit: libItem.unit }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ingredient_name || !form.vendor || !form.quantity || !form.price || !form.venture_id) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      const payload = { ...form, quantity: parseFloat(form.quantity), price: parseFloat(form.price) };
      if (editItem) {
        await api.put(`/ingredients/${editItem.purchase_id}`, payload);
        toast.success('Updated');
      } else {
        await api.post('/ingredients', payload);
        toast.success('Recorded');
      }
      onOpenChange(false);
      onSuccess();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Add'} Ingredient Purchase</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Ingredient Name</Label>
            <Autocomplete 
              suggestions={ingredientSuggestions} 
              value={form.ingredient_name} 
              onValueChange={handleIngredientChange}
              placeholder="e.g., Cream, Chocolate, Flour" 
              inputClassName="mt-1.5"
              data-testid="ingredient-name-input"
            />
          </div>
          <div>
            <Label>Vendor</Label>
            <Autocomplete 
              suggestions={vendorSuggestions} 
              value={form.vendor} 
              onValueChange={(value) => setForm({ ...form, vendor: value })}
              placeholder="Vendor name" 
              inputClassName="mt-1.5"
              data-testid="vendor-input"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div><Label>Quantity</Label><Input type="number" placeholder="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Unit</Label><Select value={form.unit} onValueChange={(value) => setForm({ ...form, unit: value })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Total Price</Label><div className="relative mt-1.5"><IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input type="number" placeholder="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="pl-9" /></div></div>
          </div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Venture</Label><Select value={form.venture_id} onValueChange={(value) => setForm({ ...form, venture_id: value, location_id: '' })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select venture" /></SelectTrigger><SelectContent>{ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Location</Label><Select value={form.location_id} onValueChange={(value) => setForm({ ...form, location_id: value })} disabled={!form.venture_id}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{filteredLocations.map((loc) => <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>)}</SelectContent></Select></div>
          <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Saving...' : editItem ? 'Update' : 'Record Purchase'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BillUploadDialog({ open, onOpenChange, ventures, locations, onSuccess, vendorLibrary = [] }) {
  const [files, setFiles] = useState([]);
  const [ventureId, setVentureId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState('');
  const [parsedItems, setParsedItems] = useState(null);
  const [billInfo, setBillInfo] = useState(null);
  const [billDate, setBillDate] = useState('');
  const [dateAutoDetected, setDateAutoDetected] = useState(false);
  const [billVendor, setBillVendor] = useState('');
  const [vendorAutoDetected, setVendorAutoDetected] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);
  
  // Create vendor suggestions from library
  const vendorSuggestions = vendorLibrary.map(v => v.name);

  const filteredLocations = locations.filter(l => l.venture_id === ventureId);

  const handleUpload = async () => {
    if (files.length === 0 || !ventureId || !locationId) { toast.error('Select file(s), venture and location'); return; }
    setParsing(true);
    try {
      let allItems = [];
      let lastBillInfo = null;
      let totalMatched = 0;

      for (let i = 0; i < files.length; i++) {
        setParseProgress(`Parsing file ${i + 1} of ${files.length}: ${files[i].name}`);
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('venture_id', ventureId);
        formData.append('location_id', locationId);
        const res = await api.post('/ingredients/parse-bill', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
        allItems = [...allItems, ...res.data.items];
        totalMatched += res.data.auto_matched || 0;
        if (res.data.vendor || res.data.date) lastBillInfo = { vendor: res.data.vendor, date: res.data.date, date_detected: res.data.date_detected, vendor_auto_matched: res.data.vendor_auto_matched };
      }

      setParsedItems(allItems);
      setBillInfo(lastBillInfo);
      // Check if date was actually detected from the bill or defaulted to today
      const today = new Date().toISOString().split('T')[0];
      const detectedDate = lastBillInfo?.date || today;
      const wasDetected = !!lastBillInfo?.date_detected;
      setBillDate(detectedDate);
      setDateAutoDetected(wasDetected);
      // Set vendor from AI detection
      const detectedVendor = lastBillInfo?.vendor || '';
      setBillVendor(detectedVendor);
      setVendorAutoDetected(!!lastBillInfo?.vendor_auto_matched || (!!detectedVendor && detectedVendor !== 'Unknown'));
      setParseProgress('');
      const matchMsg = totalMatched > 0 ? ` (${totalMatched} auto-matched to your library)` : '';
      toast.success(`Found ${allItems.length} items from ${files.length} file(s)${matchMsg}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse bill');
      setParseProgress('');
    } finally { setParsing(false); }
  };

  const handleConfirm = async () => {
    if (!parsedItems?.length) return;
    if (!billDate) { toast.error('Please select a bill date'); return; }
    if (!billVendor) { toast.error('Please select or enter a vendor name'); return; }
    setConfirming(true);
    try {
      // Apply the selected date and vendor to all items
      const itemsWithOverrides = parsedItems.map(item => ({ ...item, date: billDate, vendor: billVendor }));
      await api.post(`/ingredients/confirm-bill?venture_id=${ventureId}&location_id=${locationId}`, itemsWithOverrides);
      toast.success('Ingredients imported!');
      onOpenChange(false);
      setParsedItems(null);
      setFiles([]);
      setBillInfo(null);
      setBillDate('');
      setDateAutoDetected(false);
      setBillVendor('');
      setVendorAutoDetected(false);
      onSuccess();
    } catch { toast.error('Failed to import'); }
    finally { setConfirming(false); }
  };

  const removeItem = (idx) => setParsedItems(prev => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, field, value) => {
    setParsedItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setParsedItems(null); setFiles([]); setBillInfo(null); setBillDate(''); setDateAutoDetected(false); setBillVendor(''); setVendorAutoDetected(false); } }}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Upload Vendor Bill(s)</DialogTitle></DialogHeader>

        {!parsedItems ? (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-zinc-500">Upload one or more vendor bills (PDF, PNG, JPG) and AI will extract ingredient details automatically.</p>
            <div className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center cursor-pointer hover:border-zinc-400 transition-colors" onClick={() => fileRef.current?.click()}>
              <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-zinc-700">{files.length > 0 ? `${files.length} file(s) selected` : 'Click to upload bill(s)'}</p>
              <p className="text-xs text-zinc-400 mt-1">PDF, PNG, JPG, Excel up to 10MB each</p>
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.xlsx,.xls" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} data-testid="bill-file-input" />
            </div>
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-50 rounded-md px-3 py-1.5 text-xs">
                    <span className="text-zinc-700 truncate">{f.name}</span>
                    <button onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-500 ml-2"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Venture</Label><Select value={ventureId} onValueChange={(v) => { setVentureId(v); setLocationId(''); }}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select venture" /></SelectTrigger><SelectContent>{ventures.map((v) => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Location</Label><Select value={locationId} onValueChange={setLocationId} disabled={!ventureId}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger><SelectContent>{filteredLocations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <Button onClick={handleUpload} className="w-full" disabled={parsing || files.length === 0}>
              {parsing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{parseProgress || 'Parsing with AI...'}</> : `Upload & Parse${files.length > 1 ? ` ${files.length} Bills` : ''}`}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Bill info header with editable date and vendor */}
            <div className="bg-zinc-50 rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-4 text-sm text-zinc-600">
                <span>{parsedItems.length} items found</span>
              </div>
              {/* Vendor selection */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-zinc-600 whitespace-nowrap">Vendor:</Label>
                <Autocomplete 
                  suggestions={vendorSuggestions} 
                  value={billVendor} 
                  onValueChange={(value) => { setBillVendor(value); setVendorAutoDetected(false); }}
                  placeholder="Enter or select vendor" 
                  inputClassName="h-8 w-56"
                  data-testid="bill-vendor-input"
                />
                {vendorAutoDetected ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Auto-detected</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Please verify vendor</span>
                )}
              </div>
              {/* Date selection */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-zinc-600 whitespace-nowrap">Bill Date:</Label>
                <Input
                  type="date"
                  value={billDate}
                  onChange={(e) => { setBillDate(e.target.value); setDateAutoDetected(false); }}
                  className="h-8 w-44"
                  data-testid="bill-date-input"
                />
                {dateAutoDetected ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Auto-detected</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Please verify date</span>
                )}
              </div>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center gap-2 px-2 text-xs text-zinc-400 font-medium">
                <span className="flex-1">Item</span>
                <span className="w-14 text-center">Qty</span>
                <span className="w-7">Unit</span>
                <span className="w-20 text-center">Rate</span>
                <span className="w-16 text-center">GST</span>
                <span className="w-20 text-center">Total</span>
                <span className="w-7"></span>
              </div>
              {parsedItems.map((item, idx) => (
                <div key={idx} className={`rounded-lg text-sm p-2 ${item.price_verified === false ? 'bg-amber-50 border border-amber-200' : 'bg-zinc-50'}`} data-testid={`bill-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <Input value={item.ingredient_name} onChange={(e) => updateItem(idx, 'ingredient_name', e.target.value)} className="h-8 flex-1 min-w-0" placeholder="Name" data-testid={`bill-item-name-${idx}`} />
                    <Input type="number" value={item.quantity} onChange={(e) => {
                      const qty = parseFloat(e.target.value) || 0;
                      const ppu = parseFloat(item.price_per_unit) || 0;
                      const gst = parseFloat(item.gst_amount) || 0;
                      updateItem(idx, 'quantity', e.target.value);
                      if (ppu > 0 && qty > 0) {
                        const taxable = ppu * qty;
                        updateItem(idx, 'taxable_value', taxable.toFixed(2));
                        updateItem(idx, 'price', (taxable + gst).toFixed(2));
                      }
                    }} className="h-8 w-14 text-center px-1 flex-shrink-0" placeholder="Qty" />
                    <span className="text-zinc-400 text-xs w-7 flex-shrink-0">{item.unit}</span>
                    <div className="relative w-20 flex-shrink-0">
                      <IndianRupee className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                      <Input type="number" value={item.price_per_unit || ''} onChange={(e) => {
                        const ppu = parseFloat(e.target.value) || 0;
                        const qty = parseFloat(item.quantity) || 0;
                        const gst = parseFloat(item.gst_amount) || 0;
                        updateItem(idx, 'price_per_unit', e.target.value);
                        if (ppu > 0 && qty > 0) {
                          const taxable = ppu * qty;
                          updateItem(idx, 'taxable_value', taxable.toFixed(2));
                          updateItem(idx, 'price', (taxable + gst).toFixed(2));
                        }
                      }} className="h-8 pl-5 pr-1 text-xs" placeholder="Rate" title="Pre-GST rate per unit" />
                    </div>
                    <div className="relative w-16 flex-shrink-0">
                      <IndianRupee className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                      <Input type="number" value={item.gst_amount || ''} onChange={(e) => {
                        const gst = parseFloat(e.target.value) || 0;
                        const taxable = parseFloat(item.taxable_value) || (parseFloat(item.price_per_unit) || 0) * (parseFloat(item.quantity) || 0);
                        updateItem(idx, 'gst_amount', e.target.value);
                        if (taxable > 0) updateItem(idx, 'price', (taxable + gst).toFixed(2));
                      }} className="h-8 pl-5 pr-1 text-xs" placeholder="GST" title={item.gst_rate ? `GST @ ${item.gst_rate}%` : 'GST amount for this row'} />
                    </div>
                    <div className="relative w-20 flex-shrink-0">
                      <IndianRupee className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                      <Input type="number" value={item.price} onChange={(e) => updateItem(idx, 'price', e.target.value)} className="h-8 pl-5 pr-1 font-medium text-xs" placeholder="Total" title="GST-inclusive total paid for this row" />
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 flex-shrink-0" onClick={() => removeItem(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  {(item.auto_matched || item.price_anomaly || item.gst_rate > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5 px-0.5">
                      {item.auto_matched && item.original_name && (
                        <span className="inline-flex items-center text-[10px] leading-tight px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap max-w-full overflow-hidden" title={`Auto-matched to library (${Math.round((item.name_match_score || 0) * 100)}% match)`} data-testid={`auto-match-badge-${idx}`}>
                          <span className="truncate">✓ Was: "{item.original_name}"</span>
                        </span>
                      )}
                      {item.auto_matched && !item.original_name && (
                        <span className="inline-flex items-center text-[10px] leading-tight px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap" title="Matched to your library" data-testid={`auto-match-badge-${idx}`}>
                          ✓ In library
                        </span>
                      )}
                      {item.gst_rate > 0 && (
                        <span className="inline-flex items-center text-[10px] leading-tight px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap" title="GST rate detected from bill" data-testid={`gst-rate-badge-${idx}`}>
                          GST {item.gst_rate}%
                        </span>
                      )}
                      {item.price_anomaly && (
                        <span className="inline-flex items-center text-[10px] leading-tight px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap" title="Price differs significantly from history" data-testid={`price-anomaly-badge-${idx}`}>
                          ⚠ Unusual (was ₹{item.historical_ppu})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Bill totals breakdown */}
            <div className="bg-zinc-100 rounded-lg px-4 py-3 space-y-1.5 text-sm">
              {(() => {
                const taxableSum = parsedItems.reduce((sum, i) => sum + (parseFloat(i.taxable_value) || 0), 0);
                const discountSum = parsedItems.reduce((sum, i) => sum + (parseFloat(i.discount) || 0), 0);
                const gstSum = parsedItems.reduce((sum, i) => sum + (parseFloat(i.gst_amount) || 0), 0);
                const grandSum = parsedItems.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);
                return (
                  <>
                    {(taxableSum > 0 || gstSum > 0) && (
                      <>
                        <div className="flex items-center justify-between text-zinc-600">
                          <span>Subtotal (pre-GST)</span>
                          <span data-testid="bill-subtotal">₹{taxableSum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                        {discountSum > 0 && (
                          <div className="flex items-center justify-between text-emerald-700">
                            <span>Discount</span>
                            <span data-testid="bill-discount">− ₹{discountSum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-zinc-600">
                          <span>GST</span>
                          <span data-testid="bill-gst-total">₹{gstSum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="border-t border-zinc-300 my-1"></div>
                      </>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-zinc-700">Total amount paid</span>
                      <span className="font-semibold text-base" data-testid="bill-grand-total">₹{grandSum.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setParsedItems(null); setFiles([]); setBillInfo(null); setBillDate(''); setDateAutoDetected(false); setBillVendor(''); setVendorAutoDetected(false); }} className="flex-1">Back</Button>
              <Button onClick={handleConfirm} className="flex-1" disabled={confirming || !parsedItems.length}>
                {confirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : `Import ${parsedItems.length} Items`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function BillLibrary({ ventures, locations }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    venture_id: '', location_id: '', start_date: '', end_date: '', vendor: ''
  });
  const [viewBill, setViewBill] = useState(null);

  const fetchBills = async (f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const active = f || filters;
      if (active.venture_id) params.append('venture_id', active.venture_id);
      if (active.location_id) params.append('location_id', active.location_id);
      if (active.start_date) params.append('start_date', active.start_date);
      if (active.end_date) params.append('end_date', active.end_date);
      if (active.vendor) params.append('vendor', active.vendor);
      const res = await api.get(`/bills?${params.toString()}`);
      setBills(res.data);
    } catch (err) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBills(); }, []);

  const handleFilter = () => fetchBills();
  const clearFilters = () => {
    const cleared = { venture_id: '', location_id: '', start_date: '', end_date: '', vendor: '' };
    setFilters(cleared);
    fetchBills(cleared);
  };

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill "${bill.filename}"?`)) return;
    try {
      await api.delete(`/bills/${bill.bill_id}`);
      toast.success('Bill deleted');
      fetchBills();
    } catch {
      toast.error('Failed to delete bill');
    }
  };

  const handleView = async (bill) => {
    if (!bill.file_path) {
      toast.error('No file attached to this bill');
      return;
    }
    try {
      const res = await api.get(`/bills/${bill.bill_id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setViewBill({ ...bill, objectUrl: url });
    } catch (err) {
      toast.error(err?.response?.status === 404 ? 'Bill file is no longer available' : 'Failed to load bill file');
    }
  };

  const handleDownload = async (bill) => {
    if (!bill.file_path) {
      toast.error('No file attached to this bill');
      return;
    }
    try {
      const res = await api.get(`/bills/${bill.bill_id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = bill.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? 'Bill file is no longer available' : 'Failed to download bill');
    }
  };

  const getVentureName = (id) => ventures.find(v => v.venture_id === id)?.name || '';
  const getLocationName = (id) => locations.find(l => l.location_id === id)?.name || '';
  const filteredLocations = filters.venture_id ? locations.filter(l => l.venture_id === filters.venture_id) : locations;

  const hasFilters = filters.venture_id || filters.location_id || filters.start_date || filters.end_date || filters.vendor;

  // Group bills by month
  const billsByMonth = bills.reduce((acc, bill) => {
    const month = bill.date ? bill.date.substring(0, 7) : 'Unknown';
    if (!acc[month]) acc[month] = [];
    acc[month].push(bill);
    return acc;
  }, {});

  const formatMonth = (m) => {
    if (m === 'Unknown') return 'Unknown Date';
    const [year, month] = m.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  return (
    <div data-testid="bill-library">
      {/* Filters */}
      <Card className="roch-card mb-6">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[140px]">
              <Label className="text-xs text-zinc-500 mb-1 block">Organisation</Label>
              <Select value={filters.venture_id || 'all'} onValueChange={(v) => setFilters({ ...filters, venture_id: v === 'all' ? '' : v, location_id: '' })}>
                <SelectTrigger className="h-9" data-testid="bill-filter-venture">
                  <Building2 className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organisations</SelectItem>
                  {ventures.map(v => <SelectItem key={v.venture_id} value={v.venture_id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs text-zinc-500 mb-1 block">Location</Label>
              <Select value={filters.location_id || 'all'} onValueChange={(v) => setFilters({ ...filters, location_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-9" data-testid="bill-filter-location">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {filteredLocations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">From</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="h-9 w-[140px]" data-testid="bill-filter-start" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">To</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="h-9 w-[140px]" data-testid="bill-filter-end" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Vendor</Label>
              <Input placeholder="Search vendor..." value={filters.vendor} onChange={(e) => setFilters({ ...filters, vendor: e.target.value })} className="h-9 w-[150px]" data-testid="bill-filter-vendor" />
            </div>
            <Button size="sm" onClick={handleFilter} className="h-9" data-testid="bill-filter-apply">
              <Search className="w-3.5 h-3.5 mr-1" />Filter
            </Button>
            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="h-9 text-zinc-500">
                <X className="w-3.5 h-3.5 mr-1" />Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="roch-card metric-card">
          <div className="metric-label">Total Bills</div>
          <div className="metric-value">{bills.length}</div>
        </Card>
        <Card className="roch-card metric-card">
          <div className="metric-label">Total Amount</div>
          <div className="metric-value currency">{formatCurrency(bills.reduce((s, b) => s + (b.total_amount || 0), 0))}</div>
        </Card>
        <Card className="roch-card metric-card">
          <div className="metric-label">Vendors</div>
          <div className="metric-value">{new Set(bills.map(b => b.vendor)).size}</div>
        </Card>
      </div>

      {/* Bills grouped by month */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
      ) : bills.length === 0 ? (
        <Card className="roch-card">
          <div className="empty-state">
            <div className="empty-state-icon"><FolderOpen className="w-6 h-6" /></div>
            <h3 className="font-medium text-zinc-900 mb-1">No bills in library</h3>
            <p className="text-sm text-zinc-500">Upload bills via the "Upload Bill" button above. They'll automatically appear here.</p>
          </div>
        </Card>
      ) : (
        Object.entries(billsByMonth).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthBills]) => (
          <Card key={month} className="roch-card mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                {formatMonth(month)}
                <span className="text-xs text-zinc-400 font-normal ml-auto">{monthBills.length} bill{monthBills.length !== 1 ? 's' : ''} — {formatCurrency(monthBills.reduce((s, b) => s + (b.total_amount || 0), 0))}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="roch-table-wrap">
                <table className="roch-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>File</th>
                      <th>Vendor</th>
                      <th>Organisation</th>
                      <th>Location</th>
                      <th>Items</th>
                      <th className="text-right">Amount</th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthBills.map(bill => {
                      const hasFile = bill.file_available !== false && !!bill.file_path;
                      return (
                      <tr key={bill.bill_id}>
                        <td className="text-zinc-500">{formatDate(bill.date)}</td>
                        <td>
                          {hasFile ? (
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                              <span className="text-sm font-medium truncate max-w-[180px]" title={bill.filename}>{bill.filename}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500 border border-zinc-200 uppercase">{bill.file_ext}</span>
                            </div>
                          ) : bill.file_path ? (
                            <span className="text-xs text-amber-600 italic" title="The file is no longer available on the server">File unavailable</span>
                          ) : (
                            <span className="text-xs text-zinc-400 italic">No file attached</span>
                          )}
                        </td>
                        <td className="text-zinc-600">{bill.vendor || '—'}</td>
                        <td className="text-zinc-600">{getVentureName(bill.venture_id) || '—'}</td>
                        <td className="text-zinc-600">{getLocationName(bill.location_id) || '—'}</td>
                        <td className="text-zinc-600">{bill.items_count || 0}</td>
                        <td className="text-right font-medium">{formatCurrency(bill.total_amount || 0)}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleView(bill)} disabled={!hasFile} title={hasFile ? 'View bill' : 'No file attached'} data-testid={`view-bill-${bill.bill_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(bill)} disabled={!hasFile} title={hasFile ? 'Download bill' : 'No file attached'} data-testid={`download-bill-${bill.bill_id}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(bill)} data-testid={`delete-bill-${bill.bill_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Bill Viewer Dialog */}
      <Dialog open={!!viewBill} onOpenChange={(v) => { if (!v) { if (viewBill?.objectUrl) URL.revokeObjectURL(viewBill.objectUrl); setViewBill(null); } }}>
        <DialogContent className="sm:max-w-[800px] max-h-[85vh]" data-testid="bill-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewBill?.filename}
            </DialogTitle>
          </DialogHeader>
          {viewBill && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-600"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(viewBill.date)}</span>
                {viewBill.vendor && <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">{viewBill.vendor}</span>}
                {getVentureName(viewBill.venture_id) && <span className="px-2 py-1 rounded bg-green-50 text-green-700"><Building2 className="w-3 h-3 inline mr-1" />{getVentureName(viewBill.venture_id)}</span>}
                {getLocationName(viewBill.location_id) && <span className="px-2 py-1 rounded bg-amber-50 text-amber-700"><MapPin className="w-3 h-3 inline mr-1" />{getLocationName(viewBill.location_id)}</span>}
                <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-600 font-medium">{formatCurrency(viewBill.total_amount || 0)}</span>
              </div>
              <div className="border rounded-lg overflow-hidden bg-zinc-50" style={{ minHeight: '400px' }}>
                {['png', 'jpg', 'jpeg', 'webp'].includes(viewBill.file_ext) ? (
                  <img src={viewBill.objectUrl} alt={viewBill.filename} className="w-full object-contain max-h-[60vh]" />
                ) : viewBill.file_ext === 'pdf' ? (
                  <iframe src={viewBill.objectUrl} className="w-full h-[60vh]" title={viewBill.filename} />
                ) : (
                  <div className="flex flex-col items-center justify-center h-[300px] text-zinc-500">
                    <FileText className="w-12 h-12 mb-3 text-zinc-300" />
                    <p className="text-sm">Preview not available for .{viewBill.file_ext} files</p>
                    <Button size="sm" onClick={() => handleDownload(viewBill)} className="mt-3"><Download className="w-4 h-4 mr-1" />Download</Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
